import resourceStrings from '../../ResourceStrings';
import { BarGroup } from './BarGroup';
import { BaseCommand, CommandDefinition } from './commands';
import { O2Bars } from './O2Bars';

type DefinitionAliasType = 'o2-detailed-bar' | 'o2-detailed-unbar';

export interface O2DetailedBarUnbarSettings extends O2Bars<boolean> {
  definitionAlias: DefinitionAliasType;
}

export class O2DetailedBarUnbarCommand implements O2DetailedBarUnbarSettings {
  public voice: boolean = true;
  public gprs: boolean = true;
  public internationalCalls: boolean = false;
  public premiumCalls: boolean = false;
  public outgoingInternationalCalls: boolean = false;
  public outgoingCalls: boolean = false;
  public roaming: boolean = false;
  public smsOutgoing: boolean = false;
  public smsIncoming: boolean = false;
  public smsPremium: boolean = false;
  public mms: boolean = false;
  public wifiCalling: boolean = false;
  public enable5G: boolean = false;
  public chargeableCalls = false;

  public definition: CommandDefinition;
  public definitionAlias: DefinitionAliasType;
  public confirm: boolean;
  public key: string;
  public accountId: number | null = null;
  public networkId: number | null = null;
  public showValidationErrors: boolean;
  public stockIds: number[]; // selected SIMs within command

  constructor(command: BaseCommand) {
    this.definition = command.definition;
    this.definitionAlias = command.definition.jsonAlias as DefinitionAliasType;
    this.key = command.definition.jsonAlias;
    this.confirm = command.confirm;
    this.showValidationErrors = command.showValidationErrors;
    this.stockIds = command.stockIds;
  }

  public isValid() {
    return this.voice || this.gprs || this.hasAdvancedOptionsSelected();
  }

  public hasAdvancedOptionsSelected() {
    return (
      this.internationalCalls ||
      this.premiumCalls ||
      this.outgoingInternationalCalls ||
      this.outgoingCalls ||
      this.roaming ||
      this.smsIncoming ||
      this.smsOutgoing ||
      this.smsPremium ||
      this.mms ||
      this.wifiCalling ||
      this.enable5G ||
      this.chargeableCalls
    );
  }
}

export function getO2DetailedBarUnbarOptions(props: O2DetailedBarUnbarSettings) {
  const {
    voice,
    gprs,
    internationalCalls,
    premiumCalls,
    outgoingInternationalCalls,
    outgoingCalls,
    roaming,
    smsIncoming,
    smsOutgoing,
    smsPremium,
    mms,
    wifiCalling,
    enable5G,
    chargeableCalls,
  } = props;
  return [
    {
      label: resourceStrings.voice,
      name: 'voice',
      checked: voice,
      isAdvanced: false,
    },
    {
      label: resourceStrings.data,
      name: 'gprs',
      checked: gprs,
      isAdvanced: false,
    },
    {
      label: resourceStrings.premiumCalls,
      name: 'premiumCalls',
      checked: premiumCalls,
      isAdvanced: true,
    },
    {
      label: resourceStrings.roaming,
      name: 'roaming',
      checked: roaming,
      isAdvanced: true,
    },
    {
      label: resourceStrings.outgoingCalls,
      name: 'outgoingCalls',
      checked: outgoingCalls,
      isAdvanced: true,
    },
    {
      label: resourceStrings.internationalCalls,
      name: 'internationalCalls',
      checked: internationalCalls,
      isAdvanced: true,
    },
    {
      checked: outgoingInternationalCalls,
      isAdvanced: true,
      label: resourceStrings.outgoingInternationalCallsShort,
      name: 'outgoingInternationalCalls',
    },
    {
      label: resourceStrings.incomingSms,
      name: 'smsIncoming',
      checked: smsIncoming,
      isAdvanced: true,
    },
    {
      label: resourceStrings.outgoingSms,
      name: 'smsOutgoing',
      checked: smsOutgoing,
      isAdvanced: true,
    },
    {
      label: resourceStrings.premiumSms,
      name: 'smsPremium',
      checked: smsPremium,
      isAdvanced: true,
    },
    { label: resourceStrings.mms, name: 'mms', checked: mms, isAdvanced: true },
    {
      label: resourceStrings.wifiCalling,
      name: 'wifiCalling',
      checked: wifiCalling,
      isAdvanced: true,
    },
    {
      label: resourceStrings.enable5G,
      name: 'enable5G',
      checked: enable5G,
      isAdvanced: true,
    },
    {
      label: resourceStrings.ChargeableCallBarO2,
      name: 'chargeableCalls',
      checked: chargeableCalls,
      isAdvanced: true,
    },
  ];
}

export function getO2DetailedBarUnbarOptionGroups<T>(barSuffix: string, featureSuffix: string, props: O2Bars<T>) {
  const {
    voice,
    gprs,
    internationalCalls,
    premiumCalls,
    outgoingInternationalCalls,
    outgoingCalls,
    roaming,
    smsIncoming,
    smsOutgoing,
    smsPremium,
    mms,
    wifiCalling,
  } = props;
  const options: BarGroup<T>[] = [
    {
      group: resourceStrings.calls,
      items: [
        {
          label: resourceStrings.voice,
          name: 'voice',
          checked: voice,
          isAdvanced: false,
        },
        {
          label: resourceStrings.outgoingCalls,
          name: 'outgoingCalls',
          checked: outgoingCalls,
          isAdvanced: true,
        },
        {
          label: resourceStrings.premiumCalls,
          name: 'premiumCalls',
          checked: premiumCalls,
          isAdvanced: true,
        },
        {
          label: resourceStrings.internationalCalls,
          name: 'internationalCalls',
          checked: internationalCalls,
          isAdvanced: true,
        },
        {
          checked: outgoingInternationalCalls,
          isAdvanced: true,
          label: resourceStrings.outgoingInternationalCallsShort,
          name: 'outgoingInternationalCalls',
        },
        {
          label: resourceStrings.wifiCalling,
          name: 'wifiCalling',
          checked: wifiCalling,
          isAdvanced: true,
        },
      ],
    },
    {
      group: resourceStrings.sms,
      items: [
        {
          label: resourceStrings.incomingSms,
          name: 'smsIncoming',
          checked: smsIncoming,
          isAdvanced: true,
        },
        {
          label: resourceStrings.outgoingSms,
          name: 'smsOutgoing',
          checked: smsOutgoing,
          isAdvanced: true,
        },
        {
          label: resourceStrings.premiumSms,
          name: 'smsPremium',
          checked: smsPremium,
          isAdvanced: true,
        },
        {
          label: resourceStrings.mms,
          name: 'mms',
          checked: mms,
          isAdvanced: true,
        },
      ],
    },
    {
      group: resourceStrings.data,
      items: [{ label: 'Data', name: 'gprs', checked: gprs, isAdvanced: false }],
    },
    {
      group: resourceStrings.crossService,
      items: [
        {
          label: resourceStrings.roaming,
          name: 'roaming',
          checked: roaming,
          isAdvanced: true,
        },
      ],
    },
  ];

  return options;
}
